import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import DropdownMC from './DropdownMC';
import TeamModel from '../../components/Teams/TeamModel';

const TeamSelect = memo(({
  defaultValue,
  onSelect,
  name = 'team_id',
  placeholder,
  className = '',
  label,
  useFloatingLabel = false,
  showPersonal = false,
  teamOptions: teamOptionsProp
}) => {
  const { t } = useTranslation('TeamSelectMC');
  const [teamOptions, setTeamOptions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(defaultValue);

  // Usar traducciones como valores por defecto
  const defaultPlaceholder = placeholder || t('placeholder');
  const defaultLabel = label || t('label');

  const buildOptionSelect = useCallback((allTeams) => {
    const options = allTeams.map((team) => ({
      id: team.id,
      label: team.name,
      color: team.color
    }));

    const allTeamsOption = {
      id: 'all',
      label: t('options.all'),
      color: '#7BD6A2'
    };

    const personalOption = {
      id: 'owned',
      label: t('options.personal'),
      color: '#3CAAE1'
    };

    setTeamOptions([
      allTeamsOption,
      showPersonal ? personalOption : null,
      ...options].filter(Boolean));
  }, [t, showPersonal]);

  useEffect(() => {
    if (teamOptionsProp) {
      buildOptionSelect(teamOptionsProp);
      return;
    }

    TeamModel.get().then((res) => {
      buildOptionSelect(res.teams);
    });
  }, [buildOptionSelect, teamOptionsProp]);

  const handleTeamSelect = useCallback((teamId) => {
    setSelectedTeam(teamId);
    if (onSelect) {
      onSelect(teamId);
    }
  }, [onSelect]);

  const renderTeamOption = useCallback((option) => (
    <div className="tw-flex tw-items-center tw-w-full">
      <div
        className="tw-w-3 tw-h-3 tw-rounded-full tw-mr-3 tw-flex-shrink-0"
        style={{ backgroundColor: option.color }}
      />
      <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
        {option.label}
      </span>
    </div>
  ), []);

  const renderSelectedTeam = useCallback((option) => (
    <div className="tw-flex tw-items-center tw-w-full">
      {option?.color && (
        <div
          className="tw-w-3 tw-h-3 tw-rounded-full tw-mr-3 tw-flex-shrink-0"
          style={{ backgroundColor: option.color }}
        />
      )}
      <span className="tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
        {option?.label || defaultPlaceholder}
      </span>
    </div>
  ), [defaultPlaceholder]);

  const dropdownProps = useMemo(() => ({
    id: "team-select",
    name,
    label: defaultLabel,
    options: teamOptions,
    defaultValue: selectedTeam,
    onSelect: handleTeamSelect,
    placeholder: defaultPlaceholder,
    className,
    useFloatingLabel,
    renderOption: renderTeamOption,
    renderSelected: renderSelectedTeam,
    search: false
  }), [
    name,
    defaultLabel,
    teamOptions,
    selectedTeam,
    handleTeamSelect,
    defaultPlaceholder,
    className,
    useFloatingLabel,
    renderTeamOption,
    renderSelectedTeam
  ]);

  return <DropdownMC {...dropdownProps} />;
});

TeamSelect.displayName = 'TeamSelect';

export default TeamSelect;
